import * as React from "react"
import { ComponentProps, memo, ReactNode, useMemo, useEffect, useState } from "react"
import { KeyboardAvoidingView, Platform, StyleSheet, Keyboard } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view"
import HeaderHeightContext from "@react-navigation/elements/src/Header/HeaderHeightContext"

type Props = {
  children: ReactNode
  style?: ComponentProps<typeof KeyboardAvoidingView>["style"]
  behavior?: "height" | "position" | "padding" | undefined
  noContainer?: boolean
  useAware?: boolean
}

export const useKeyboardVisible = () => {
  const [isKeyboardVisible, setKeyboardVisible] = useState(false)

  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener("keyboardDidShow", () => {
      setKeyboardVisible(true)
    })
    const keyboardDidHideListener = Keyboard.addListener("keyboardDidHide", () => {
      setKeyboardVisible(false)
    })

    return () => {
      keyboardDidHideListener.remove()
      keyboardDidShowListener.remove()
    }
  }, [])

  return isKeyboardVisible
}

export const CustomKeyboardAvoidingView = memo<Props>(({ children, style, behavior = "padding", noContainer, useAware }) => {
  const insets = useSafeAreaInsets()
  const headerHeight = React.useContext(HeaderHeightContext) ?? 0
  const isKeyboardVisible = useKeyboardVisible()

  // SafeArenaInsets value is wrong on Iphone SE => Set fix value 56
  const overallInsetHeight = useMemo(
    () => Platform.select({ ios: Math.max(56, insets.top + insets.bottom), android: isKeyboardVisible ? 0 : headerHeight }),
    [insets, headerHeight, isKeyboardVisible]
  )
  return (
    <>
      {Platform.OS === "web" && useAware ? (
        <KeyboardAwareScrollView
          contentContainerStyle={{ flexGrow: 1 }}
          keyboardShouldPersistTaps={"handled"}
          enableOnAndroid={true}
          extraHeight={180}
          keyboardOpeningTime={0}
          enableResetScrollToCoords={false}
        >
          {children}
        </KeyboardAwareScrollView>
      ) : (
        <KeyboardAvoidingView
          behavior={Platform.OS !== "web" ? behavior : undefined}
          style={[noContainer ? null : styles.container, style]}
          enabled
          keyboardVerticalOffset={behavior === "padding" ? overallInsetHeight : undefined}
        >
          {children}
        </KeyboardAvoidingView>
      )}
    </>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
})
